import React from 'react';
import { motion } from 'framer-motion';
import { BsRobot, BsGlobe, BsPeople } from 'react-icons/bs';

interface OverviewTabProps {
    activeProjects: any[];
    financials: any[];
    dashboardMetrics?: any;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ activeProjects, financials, dashboardMetrics }) => {
    const aiInsight = dashboardMetrics?.spendingOptimizationInsight || "You need active spending and matched talent to receive insights.";
    const executiveSummary = dashboardMetrics?.aiIntelligence?.executiveSummary || aiInsight;
    const riskFlags = dashboardMetrics?.aiIntelligence?.riskFlags || [];
    const synergy = Math.round(dashboardMetrics?.synergyScore || 0);
    const geos = dashboardMetrics?.geoIntelligence || [];

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Left Column: Traditional Overview (Current Projects & Financials) */}
            <div className="lg:col-span-2 space-y-8">
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-serif font-bold text-white">Active Projects Timeline</h3>
                        <button className="text-xs text-premium-gold hover:underline">View All</button>
                    </div>
                    <div className="space-y-4">
                        {activeProjects.length > 0 ? activeProjects.map((p, i) => (
                            <div key={i} className="flex justify-between items-center p-4 bg-black/20 rounded-lg hover:border-premium-gold/30 border border-transparent transition-colors cursor-pointer">
                                <div>
                                    <div className="font-bold text-white mb-1">{p.name || 'Untitled Project'}</div>
                                    <div className="text-xs text-white/50">Due: {p.dueDate ? new Date(p.dueDate).toLocaleDateString() : 'N/A'}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-premium-gold font-bold">{p.progress || 0}%</div>
                                    <div className="text-[10px] text-white/40 uppercase tracking-wider">Progress</div>
                                </div>
                            </div>
                        )) : (
                            <div className="text-white/40 italic p-4 text-center">No active projects</div>
                        )}
                    </div>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <h3 className="text-xl font-serif font-bold text-white mb-6">Recent Transactions</h3>
                    <div className="space-y-2">
                        {financials.slice(0, 5).map((f, i) => (
                            <div key={i} className="flex justify-between items-center p-3 bg-black/10 rounded-lg hover:bg-white/5 transition-colors">
                                <div>
                                    <div className="text-sm text-white/80 font-medium">{f.project}</div>
                                    <div className="text-xs text-white/40">{new Date(f.date).toLocaleDateString()} • {f.status}</div>
                                </div>
                                <div className="text-premium-gold font-bold">${f.amount.toLocaleString()}</div>
                            </div>
                        ))}
                        {financials.length === 0 && <div className="text-white/40 italic text-center p-4">No financial records</div>}
                    </div>
                </div>
            </div>

            {/* Right Column: Analytics & AI Intelligence */}
            <div className="space-y-6">
                <h3 className="text-xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-premium-gold to-white mb-2 flex items-center gap-2">
                    <BsRobot className="text-premium-gold" /> AI Intelligence
                </h3>

                {/* AI Executive Summary */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-gradient-to-br from-premium-gold/10 to-transparent border border-premium-gold/30 rounded-xl p-6 relative overflow-hidden group"
                >
                    <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-premium-gold/20 rounded-lg"><BsRobot className="text-premium-gold" /></div>
                        <h4 className="font-bold text-white text-sm">Executive Portfolio Summary</h4>
                    </div>
                    <p className="text-sm text-white/70 leading-relaxed mb-4">
                        {executiveSummary}
                    </p>
                    
                    {riskFlags.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                            <h5 className="text-xs font-bold text-white/40 uppercase tracking-wider mb-2">Identified Risks</h5>
                            {riskFlags.map((risk: any, idx: number) => (
                                <div key={idx} className="bg-black/40 border border-white/5 rounded p-3 text-sm">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="font-bold text-white">{risk.project}</span>
                                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${risk.riskLevel === 'High' ? 'bg-red-500/20 text-red-400' : risk.riskLevel === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'}`}>
                                            {risk.riskLevel}
                                        </span>
                                    </div>
                                    <div className="text-white/60 text-xs mb-2">{risk.description}</div>
                                    <div className="text-premium-gold text-xs italic">Action: {risk.suggestedAction}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="mt-4">
                        <button
                            onClick={() => {
                                const financialsTab = document.querySelector('#client-tabs button:nth-child(3)') as HTMLElement;
                                if (financialsTab) financialsTab.click();
                            }}
                            className="text-xs text-premium-wood-dark font-bold bg-premium-gold px-4 py-2 rounded shadow-[0_0_15px_rgba(212,175,55,0.4)] hover:bg-white transition-colors"
                        >
                            Analyze Budget
                        </button>
                    </div>
                </motion.div>

                {/* Talent Synergy Score */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white/5 border border-white/10 rounded-xl p-6"
                >
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-500/20 rounded-lg"><BsPeople className="text-blue-400" /></div>
                            <h4 className="font-bold text-white text-sm">Team Synergy</h4>
                        </div>
                        <ScoreCircle score={synergy} />
                    </div>
                    {synergy > 0 ? (
                        <p className="text-xs text-white/50 mb-3">Your active talent pool has an <strong className="text-white">{synergy}% compatibility</strong> rating based on work styles, complementary skills, and past overlap.</p>
                    ) : (
                        <p className="text-xs text-white/50 mb-3">Hire more talent across your projects to calculate synergy scores.</p>
                    )}
                </motion.div>

                {/* Geo Hiring Intelligence */}
                {geos.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 }}
                        className="bg-white/5 border border-white/10 rounded-xl p-6"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-green-500/20 rounded-lg"><BsGlobe className="text-green-400" /></div>
                            <h4 className="font-bold text-white text-sm">Geo Intelligence</h4>
                        </div>
                        <ul className="space-y-3 text-sm">
                            {geos.map((geo: any, idx: number) => (
                                <li key={idx} className="flex justify-between items-center">
                                    <span className="text-white/70">{geo.location}</span>
                                    <span className="text-xs text-green-400 font-bold">{geo.highlight}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-4 pt-4 border-t border-white/10 text-xs text-white/40 italic">
                            Data based on current network map.
                        </div>
                    </motion.div>
                )}

            </div>
        </div>
    );
};

// Helper component for radial score
const ScoreCircle = ({ score }: { score: number }) => {
    const radius = 18;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (score / 100) * circumference;

    return (
        <div className="relative w-12 h-12 flex items-center justify-center">
            <svg className="w-full h-full transform -rotate-90">
                <circle cx="24" cy="24" r={radius} className="stroke-white/10" strokeWidth="4" fill="none" />
                <circle
                    cx="24" cy="24" r={radius}
                    className="stroke-premium-gold"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                    style={{ strokeDasharray: circumference, strokeDashoffset }}
                />
            </svg>
            <span className="absolute text-xs font-bold text-white">{score}</span>
        </div>
    );
}

export default OverviewTab;
